import { EventManager, models as eventManagerModels } from '@kurtosys/event-manager';
import { IBaseEventDetail } from '@kurtosys/types/eventBus/index.js';

interface EventDetail extends IBaseEventDetail {
	identifier: string;
}
export type PagingAction = 'First' | 'Previous' | 'Next' | 'Last' | 'PageNumber' | 'PageSize';
export interface UpdatePaginationDetail extends EventDetail {
	pagingData?: PagingData;
}
export interface PaginationChangeDetail extends EventDetail {
	action: PagingAction; // indicator of what button was pressed
	newPagingData: PagingData;
	oldPagingData?: PagingData;
}
export interface PagingData {
	pageSize: number; // linked to limit on the API request body
	pageIndex?: number;
	recordIndex: number; // linked to start on the API request body
	totalRecords: number; // linked to start on the API response body
}
type EventCallback<T extends eventManagerModels.EventTypeDetail> = (ev: eventManagerModels.EventPublish<T>) => void;

if (customElements.get('ksys-event-bus') === undefined) {
	class KsysEventBus extends HTMLElement {
		constructor() {
			super();
			this.attachShadow({ mode: 'open' });
		}
	}
	customElements.define('ksys-event-bus', KsysEventBus);
}

export class StorybookEventBus {
	private static manager: EventManager | undefined;

	static getEventManager() {
		if (!StorybookEventBus.manager) {
			EventManager.init();
			StorybookEventBus.manager = EventManager.get();
		}
		return StorybookEventBus.manager;
	}

	static triggerUpdatePagination(
		eventDetail: Omit<eventManagerModels.detail.EventUpdatePaginationDetail, 'type'>,
		eventSource: eventManagerModels.EventSource,
	) {
		const em = StorybookEventBus.getEventManager();
		if (em) {
			em.publish(eventSource.applicationId || eventSource.templateId, eventSource, {
				type: eventManagerModels.EventTypes.UPDATE_PAGINATION,
				...eventDetail
			});
		}
	}

	static registerUpdatePagination(
		targetEventSourceKey: string,
		eventListener: eventManagerModels.EventListener,
		callback: EventCallback<eventManagerModels.detail.EventUpdatePaginationDetail>,
	) {
		StorybookEventBus.registerEvent(targetEventSourceKey, eventManagerModels.EventTypes.UPDATE_PAGINATION, eventListener, callback);
	}

	static triggerPaginationOnChange(
		eventDetail: Omit<eventManagerModels.detail.EventPaginationChangeDetail, 'type'>,
		eventSource: eventManagerModels.EventSource,
	) {
		const em = StorybookEventBus.getEventManager();
		if (em) {
			em.publish(eventSource.applicationId || eventSource.templateId, eventSource, {
				type: eventManagerModels.EventTypes.PAGINATION_CHANGE,
				...eventDetail
			});
		}
	}

	static registerPaginationOnChange(
		targetEventSourceKey: string,
		eventListener: eventManagerModels.EventListener,
		callback: EventCallback<eventManagerModels.detail.EventPaginationChangeDetail>,
	) {
		StorybookEventBus.registerEvent(targetEventSourceKey, eventManagerModels.EventTypes.PAGINATION_CHANGE, eventListener, callback);
	}

	private static registerEvent<T extends eventManagerModels.EventTypeDetail>(
		targetEventSourceKey: string,
		type: eventManagerModels.EventTypes,
		eventListener: eventManagerModels.EventListener,
		callback: EventCallback<T>,
	) {
		const em = StorybookEventBus.getEventManager();
		if (em) {
			em.subscribe(targetEventSourceKey, {
				type,
				listener: eventListener,
				onPublish: async (ev: eventManagerModels.EventPublish<T>) => {
					const { source } = ev;
					if (targetEventSourceKey === source.applicationId) {
						callback(ev);
					}
				},
			});
		}
	}
}
